'use client';

import { c as _c } from "react/compiler-runtime";
import { m, useReducedMotion } from 'motion/react';
import { useLocale } from 'next-intl';
import type { MouseEvent } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01IconSolid, ArrowUp01IconSolid } from '@/components/ui/icon/glyphs';
import { formatCountFor } from '@/lib/format/format-count';
import { MOTION_TAP } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
const REACTION_COUNT_ANIMATION_DURATION_MS = 300 as const;
const VOTE_ARROW_TAP = {
  scale: 0.97,
  transition: MOTION_TAP
} as const;
export interface ReactionVoteButtonsProps {
  reaction: 'up' | 'down' | null;
  upCount: number;
  downCount: number;
  upAriaLabel: string;
  downAriaLabel: string;
  onReact: (next: 'up' | 'down', e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  size?: 'md' | 'sm';
}
export function ReactionVoteButtons(t0) {
  const $ = _c(40);
  const {
    reaction,
    upCount,
    downCount,
    upAriaLabel,
    downAriaLabel,
    onReact,
    disabled: t1,
    className,
    size: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const prefersReducedMotion = useReducedMotion();
  const t3 = useLocale();
  let t4;
  if ($[0] !== t3) {
    t4 = formatCountFor(t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const formatCount = t4;
  const net = upCount - downCount;
  const isSmall = size === "sm";
  const wrapperHeight = isSmall ? "h-6" : "h-8";
  const buttonCellSize = isSmall ? "h-6 w-5 p-0" : "h-8 w-6 p-0";
  const arrowSize = isSmall ? "size-3.5" : "size-4";
  let t5;
  if ($[2] !== className || $[3] !== wrapperHeight) {
    t5 = cn("inline-flex items-center gap-0 px-1 text-foreground-secondary", wrapperHeight, className);
    $[2] = className;
    $[3] = wrapperHeight;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onReact) {
    t6 = e => onReact("up", e);
    $[5] = onReact;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = reaction === "up";
  let t8;
  if ($[7] !== arrowSize || $[8] !== prefersReducedMotion || $[9] !== reaction) {
    t8 = prefersReducedMotion === true ? <Icon icon={ArrowUp01IconSolid} className={cn(arrowSize, reaction === "up" ? "fill-rating-good text-rating-good" : "text-foreground-secondary")} /> : <m.span className="inline-flex origin-center" whileTap={VOTE_ARROW_TAP}><Icon icon={ArrowUp01IconSolid} className={cn(arrowSize, reaction === "up" ? "fill-rating-good text-rating-good" : "text-foreground-secondary")} /></m.span>;
    $[7] = arrowSize;
    $[8] = prefersReducedMotion;
    $[9] = reaction;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== buttonCellSize || $[12] !== disabled || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== upAriaLabel) {
    t9 = <AnimatedButton variant="tertiary" size="sm" disableTapScale={true} onClick={t6} disabled={disabled} aria-label={upAriaLabel} aria-pressed={t7} className={buttonCellSize}>{t8}</AnimatedButton>;
    $[11] = buttonCellSize;
    $[12] = disabled;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = upAriaLabel;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== formatCount || $[19] !== net) {
    t10 = <output className="-mx-0.5 min-w-6 shrink-0 text-center text-body font-medium tabular-nums text-foreground-primary"><AnimatedNumber value={net} format={formatCount} duration={REACTION_COUNT_ANIMATION_DURATION_MS} /></output>;
    $[18] = formatCount;
    $[19] = net;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== onReact) {
    t11 = e_0 => onReact("down", e_0);
    $[21] = onReact;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = reaction === "down";
  let t13;
  if ($[23] !== arrowSize || $[24] !== prefersReducedMotion || $[25] !== reaction) {
    t13 = prefersReducedMotion === true ? <Icon icon={ArrowDown01IconSolid} className={cn(arrowSize, reaction === "down" ? "fill-rating-bad text-rating-bad" : "text-foreground-secondary")} /> : <m.span className="inline-flex origin-center" whileTap={VOTE_ARROW_TAP}><Icon icon={ArrowDown01IconSolid} className={cn(arrowSize, reaction === "down" ? "fill-rating-bad text-rating-bad" : "text-foreground-secondary")} /></m.span>;
    $[23] = arrowSize;
    $[24] = prefersReducedMotion;
    $[25] = reaction;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== buttonCellSize || $[28] !== disabled || $[29] !== downAriaLabel || $[30] !== t11 || $[31] !== t12 || $[32] !== t13) {
    t14 = <AnimatedButton variant="tertiary" size="sm" disableTapScale={true} onClick={t11} disabled={disabled} aria-label={downAriaLabel} aria-pressed={t12} className={buttonCellSize}>{t13}</AnimatedButton>;
    $[27] = buttonCellSize;
    $[28] = disabled;
    $[29] = downAriaLabel;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== size || $[35] !== t10 || $[36] !== t14 || $[37] !== t5 || $[38] !== t9) {
    t15 = <div data-size={size} className={t5}>{t9}{t10}{t14}</div>;
    $[34] = size;
    $[35] = t10;
    $[36] = t14;
    $[37] = t5;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}